import {
  collection,
  addDoc,
  query,
  where,
  updateDoc,
  doc,
  serverTimestamp,
  orderBy,
  limit,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import { getUserData } from '../../../pages/auth/utils/firebase/userOperations';
import { getDisplayName } from '../../../utils/userDisplay';


class NotificationService {
  constructor() {
    this.activeListeners = new Map();
    this.aggregationTimeouts = new Map();
    this.preferences = null;
    this.actorCache = new Map();
    this.actorCacheTTL = 300000;
  }

  setPreferences(preferences) {
    this.preferences = preferences || null;
  }

  /**
   * Create a new notification with intelligent aggregation
   */
  async createNotification(data) {
    try {
      // Don't create notification if user is notifying themselves
      if (data.userId === data.actorId) {
        return null;
      }

      const notification = await this.prepareNotificationData(data);
      // NOTE: Client-side aggregation has been removed because Firestore rules
      // block users from reading other users' notifications (MED-6). 
      // Aggregation remains a backend concern via Cloud Functions.
      return await this.createNewNotification(notification);
    } catch (error) {
      console.error('Error creating notification:', error);
      throw error;
    }
  }

  /**
   * Prepare notification data with rich content
   */
  async prepareNotificationData(data) {
    const { userId, actorId, type, postId, commentId, solutionId, projectId } = data;

    const actorData = await this.getActorProfile(actorId);
    const actorName = getDisplayName({ profile: actorData, candidates: [actorData?.username, actorData?.displayName, actorData?.name] });

    // Generate notification content based on type
    const content = this.generateNotificationContent(type, actorName, data);

    return {
      userId,
      actorId,
      type,
      category: this.getNotificationCategory(type),
      priority: this.getNotificationPriority(type),

      // Content
      title: content.title,
      message: content.message,
      previewText: content.preview,
      actionUrl: content.actionUrl,

      // References
      postId: postId || null,
      commentId: commentId || null,
      solutionId: solutionId || null,
      projectId: projectId || null,

      // Aggregation
      isAggregated: false,
      aggregationKey: this.generateAggregationKey({ type, postId, commentId, solutionId, projectId }),
      actorIds: [actorId],

      // Metadata
      read: false,
      createdAt: serverTimestamp(),
      readAt: null,
      expiresAt: this.getExpirationDate(type),
      deleted: false,
      deletedAt: null,
      deletedBy: null,
      purgeAfter: null
    };
  }

  async getActorProfile(actorId) {
    if (!actorId) return null;
    const cached = this.actorCache.get(actorId);
    const now = Date.now();
    if (cached && (now - cached.t) < this.actorCacheTTL) {
      // LRU Eviction: bump to end of Map to mark as recently used
      this.actorCache.delete(actorId);
      this.actorCache.set(actorId, { v: cached.v, t: now });
      return cached.v;
    }
    try {
      const data = await getUserData(actorId);
      
      // LRU Eviction: enforce hard capacity (50) to prevent memory leak
      if (this.actorCache.size >= 50) {
        // Map iterates in insertion order, so the first key is the oldest
        const oldestKey = this.actorCache.keys().next().value;
        this.actorCache.delete(oldestKey);
      }
      
      this.actorCache.set(actorId, { v: data, t: now });
      return data;
    } catch (_) {
      return null;
    }
  }

  /**
   * Generate notification content based on type
   */
  generateNotificationContent(type, actorName, data) {
    switch (type) {
      case 'like':
        return {
          title: `${actorName} liked your ${data.targetType || 'post'}`,
          message: `${actorName} liked your ${data.targetType || 'post'}`,
          preview: data.postTitle ? `"${data.postTitle.substring(0, 50)}..."` : '',
          actionUrl: data.postId ? `/home?post=${data.postId}` : '/home'
        };

      case 'comment':
        return {
          title: `${actorName} commented on your post`,
          message: `${actorName} commented on your post`,
          preview: data.commentText ? `"${data.commentText.substring(0, 100)}..."` : '',
          actionUrl: data.postId ? `/home?post=${data.postId}` : '/home'
        };

      case 'reply':
        return {
          title: `${actorName} replied to your comment`,
          message: `${actorName} replied to your comment`,
          preview: data.replyText ? `"${data.replyText.substring(0, 100)}..."` : '',
          actionUrl: data.postId ? `/home?post=${data.postId}` : '/home'
        };

      case 'follow':
        return {
          title: `${actorName} started following you`,
          message: `${actorName} started following you`,
          preview: '',
          actionUrl: `/profile/${data.actorId}`
        };

      case 'follow_request':
        return {
          title: `${actorName} requested to follow you`,
          message: `${actorName} wants to follow you`,
          preview: '',
          actionUrl: '/notifications'
        };

      case 'follow_request_accepted':
        return {
          title: `${actorName} accepted your follow request`,
          message: `${actorName} accepted your follow request`,
          preview: '',
          actionUrl: `/profile/${data.actorId}`
        };

      case 'follow_back':
        return {
          title: `${actorName} followed you back! 🎉`,
          message: `${actorName} followed you back!`,
          preview: 'You both follow each other now',
          actionUrl: `/profile/${data.actorId}`
        };

      case 'mention':
        return {
          title: `${actorName} mentioned you`,
          message: `${actorName} mentioned you in a ${data.targetType || 'post'}`,
          preview: data.mentionText ? `"${data.mentionText.substring(0, 100)}..."` : '',
          actionUrl: data.postId ? `/home?post=${data.postId}` : '/home'
        };

      case 'share':
        return {
          title: `${actorName} shared your post`,
          message: `${actorName} shared your post`,
          preview: data.postTitle ? `"${data.postTitle.substring(0, 50)}..."` : '',
          actionUrl: data.postId ? `/home?post=${data.postId}` : '/home'
        };

      case 'solution_vote':
        return {
          title: `${actorName} voted on your solution`,
          message: `${actorName} ${data.voteType === 'up' ? 'upvoted' : 'downvoted'} your solution`,
          preview: data.solutionTitle ? `"${data.solutionTitle.substring(0, 50)}..."` : '',
          actionUrl: data.solutionId ? `/solutions/${data.solutionId}` : '/solutions'
        };

      case 'project_invite':
        return {
          title: `${actorName} invited you to a project`,
          message: `${actorName} invited you to join "${data.projectTitle || 'a project'}"`,
          preview: data.projectDescription ? `"${data.projectDescription.substring(0, 100)}..."` : '',
          actionUrl: '/notifications'
        };

      case 'project_invite_accepted':
        return {
          title: `${actorName} accepted your project invitation`,
          message: `${actorName} accepted your invitation to "${data.projectTitle || 'your project'}"`,
          preview: '',
          actionUrl: data.projectId ? `/projects/${data.projectId}` : '/projects'
        };

      case 'project_launched':
        return {
          title: `Project "${data.projectTitle}" has launched`,
          message: `The project "${data.projectTitle || 'you\'re part of'}" has been launched`,
          preview: '',
          actionUrl: data.projectId ? `/projects/${data.projectId}` : '/projects'
        };

      case 'message':
        return {
          title: `${actorName} sent you a message`,
          message: `${actorName} sent you a message`,
          preview: data.messageText ? `"${data.messageText.substring(0, 100)}..."` : '',
          actionUrl: data.conversationId ? `/home?conversationId=${data.conversationId}` : `/home`
        };

      default:
        return {
          title: 'New notification',
          message: data.message || 'You have a new notification',
          preview: '',
          actionUrl: '/notifications'
        };
    }
  }

  /**
   * Get notification category
   */
  getNotificationCategory(type) {
    const socialTypes = ['like', 'comment', 'reply', 'follow', 'follow_request', 'follow_request_accepted', 'follow_back', 'mention', 'share', 'message'];
    const projectTypes = ['project_invite', 'project_invite_accepted', 'project_launched', 'project_update'];
    const solutionTypes = ['solution_vote', 'solution_reason', 'solution_featured'];

    if (socialTypes.includes(type)) return 'social';
    if (projectTypes.includes(type)) return 'project';
    if (solutionTypes.includes(type)) return 'solution';
    return 'system';
  }

  /**
   * Get notification priority
   */
  getNotificationPriority(type) {
    const highPriority = ['project_invite', 'message', 'mention', 'follow_request'];
    const lowPriority = ['like', 'follow', 'follow_request_accepted', 'follow_back'];

    if (highPriority.includes(type)) return 'high';
    if (lowPriority.includes(type)) return 'low';
    return 'medium';
  }

  /**
   * Generate aggregation key for grouping similar notifications
   */
  generateAggregationKey(data) {
    const { type, postId, commentId, solutionId, projectId } = data;

    // Group notifications by type and target
    if (postId) return `${type}_post_${postId}`;
    if (commentId) return `${type}_comment_${commentId}`;
    if (solutionId) return `${type}_solution_${solutionId}`;
    if (projectId) return `${type}_project_${projectId}`;

    return `${type}_general`;
  }

  /**
   * Create new notification
   */
  async createNewNotification(notification) {
    try {
      const docRef = await addDoc(collection(db, 'notifications'), notification);
      return { id: docRef.id, created: true };
    } catch (error) {
      console.error('Error creating new notification:', error);
      throw error;
    }
  }

  /**
   * Get expiration date for notification
   */
  getExpirationDate(type) {
    // Most notifications expire after 30 days
    const defaultExpiry = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

    switch (type) {
      case 'project_invite':
        // Project invites expire after 7 days
        return new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
      case 'message':
        // Messages don't expire
        return null;
      default:
        return defaultExpiry;
    }
  }

  /**
   * Subscribe to real-time notifications for a user with enhanced error handling
   */
  subscribeToNotifications(userId, onNotificationUpdate, onError = null) {
    // Prevent double subscriptions
    if (this.activeListeners.has(userId)) {
      console.warn(`Notification listener already exists for user: ${userId}`);
      return this.activeListeners.get(userId);
    }

    // Force cleanup of any existing listener (safety measure)
    this.unsubscribeFromNotifications(userId);

    try {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(20) // Reduced initial load for better performance
      );

      let retryCount = 0;
      const maxRetries = 3;

      const createListener = () => {
        const unsubscribe = onSnapshot(
          q,
          {
            // Add options to handle network issues better
            includeMetadataChanges: false // Avoid metadata-only changes
          },
          (snapshot) => {
            try {
              // Process changes more carefully to avoid conflicts
              const changes = snapshot.docChanges();

              changes.forEach((change) => {
                try {
                  const notification = {
                    id: change.doc.id,
                    ...change.doc.data()
                  };

                  // Skip deleted notifications, but explicitly remove them from UI first
                  if (notification.deleted) {
                    if (change.type === 'modified' || change.type === 'added') {
                      onNotificationUpdate('removed', notification);
                    }
                    return;
                  }

                  // Only process recent changes to avoid conflicts
                  const isRecent = notification.createdAt?.toDate ?
                    (Date.now() - notification.createdAt.toDate().getTime() < 60000) : // 1 minute
                    true;

                  if (change.type === 'added') {
                    onNotificationUpdate('added', notification);

                    // Show desktop notification only for very recent notifications
                    if (isRecent && this.shouldShowDesktopNotification(notification)) {
                      this.showDesktopNotification(notification);
                    }
                  } else if (change.type === 'modified') {
                    onNotificationUpdate('modified', notification);
                  } else if (change.type === 'removed') {
                    onNotificationUpdate('removed', notification);
                  }
                } catch (docError) {
                  console.error('Error processing notification document:', docError);
                }
              });
            } catch (snapshotError) {
              console.error('Error processing notification snapshot:', snapshotError);
            }
          },
          (error) => {
            console.error('Notification listener error:', error);

            // Clean up current listener
            this.unsubscribeFromNotifications(userId);

            // Retry with exponential backoff
            if (retryCount < maxRetries) {
              retryCount++;
              const delay = Math.pow(2, retryCount) * 1000; // 2s, 4s, 8s
              console.log(`Retrying notification listener in ${delay}ms (attempt ${retryCount})`);

              setTimeout(() => {
                if (!this.activeListeners.has(userId)) {
                  createListener();
                }
              }, delay);
            } else {
              console.error('Max retries reached for notification listener');
              if (onError) onError(error);
            }
          }
        );

        this.activeListeners.set(userId, unsubscribe);
        console.log(`Notification listener created for user: ${userId}`);
        return unsubscribe;
      };

      return createListener();

    } catch (error) {
      console.error('Error creating notification listener:', error);
      if (onError) onError(error);
      return null;
    }
  }

  /**
   * Unsubscribe from notifications
   */
  unsubscribeFromNotifications(userId) {
    const unsubscribe = this.activeListeners.get(userId);
    if (unsubscribe) {
      try {
        unsubscribe();
        console.log(`Notification listener cleaned up for user: ${userId}`);
      } catch (error) {
        console.error('Error unsubscribing notification listener:', error);
      } finally {
        this.activeListeners.delete(userId);
      }
    }
  }

  /**
   * Show desktop notification
   */
  shouldShowDesktopNotification(notification) {
    if (notification.read) return false;
    const p = this.preferences;
    if (p && p.desktop === false) return false;
    const map = {
      like: 'likes',
      comment: 'comments',
      reply: 'replies',
      follow: 'follows',
      mention: 'mentions',
      share: null,
      message: 'messages',
      project_invite: 'projects',
      project_invite_accepted: 'projects',
      project_launched: 'projects',
      solution_vote: 'solutions',
      solution_reason: 'solutions'
    };
    const key = map[notification.type];
    if (p && p.types && key && p.types[key] === false) return false;

    const notificationTime = notification.createdAt?.toDate?.() || new Date(notification.createdAt);
    const isRecent = Date.now() - notificationTime.getTime() < 60 * 1000;
    return isRecent;
  }

  /**
   * Show desktop notification
   */
  showDesktopNotification(notification) {
    const p = this.preferences;
    if (p && p.desktop === false) return;
    if (!('Notification' in window)) return;

    if (Notification.permission === 'granted') {
      new Notification(notification.title, {
        body: notification.message,
        icon: '/favicon.ico',
        tag: notification.id,
        data: { actionUrl: notification.actionUrl },
        requireInteraction: notification.priority === 'high'
      });
    } else if (Notification.permission !== 'denied') {
      if (p && p.desktop === false) return;
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          this.showDesktopNotification(notification);
        }
      });
    }
  }

  /**
   * Clean up resources
   */
  cleanup() {
    // Clear all active listeners
    this.activeListeners.forEach(unsubscribe => {
      try { unsubscribe(); } catch { }
    });
    this.activeListeners.clear();

    // Clear aggregation timeouts
    this.aggregationTimeouts.forEach(timeout => {
      clearTimeout(timeout);
    });
    this.aggregationTimeouts.clear();
  }
}

// Export singleton instance with name
const notificationService = new NotificationService();
export default notificationService;
